// An app template as more than one page shows it (RFC-052 "Web",
// byj-1wv.51.20): the lines that publish one and make an app from one, how one
// is published step by step, and a template's row with its menu — used by
// /templates, a template's page and a class's pages, so none imports another.
import { appTemplateArchiveURL, type AppTemplate } from "../api";
import { download } from "../download";
import { t, when, whenShort, phrases } from "../i18n";
import { navigate } from "../router";
import { AuthorCommands } from "./CommandLine";
import { DocText } from "./DocText";
import { Link } from "./Nav";
import { RowMenu, type MenuItem } from "./RowMenu";

/** What publishes a project as a template, run in that project. */
export const publishCommand = "byj app publish --template NAME";

/**
 * The line that makes an app from a template. Mine by its name; one shared
 * with me by its id, since a name resolves to my own template first and is
 * refused when two I may use share it (RFC-052 §4).
 */
export function createCommand(tpl: Pick<AppTemplate, "id" | "name" | "mine">): string {
  return `byj app create --template ${tpl.mine ? tpl.name : tpl.id} --name my-app`;
}

/** What signs this computer in to Share, which publishing needs (byj-share sends the template). */
export const loginCommand = "byj-share login";

/**
 * How a template is published, step by step, as RFC-052 §3 does it: sign in
 * once, run the line in the project — what byj shows, asks and refuses —
 * the manifest the first run writes, and where the version then appears;
 * then the guide's section for the rest. `name` is the template's, on its
 * own page; NAME otherwise.
 */
export function PublishSteps({ name }: { name?: string }) {
  return (
    <div className="stack publish-steps" data-testid="publish-steps">
      <ol className="steps">
        <li>
          <p className="flush">{t("templates:publishStepLogin")}</p>
          <AuthorCommands commands={[loginCommand]} />
        </li>
        <li>
          <p className="flush">{t("templates:publishStepRun")}</p>
          <AuthorCommands commands={[name ? `byj app publish --template ${name}` : publishCommand]} />
          <p className="muted small flush">{t("templates:publishStepRunWhat")}</p>
        </li>
        <li>
          <p className="flush">
            <DocText s={t("templates:publishStepManifest")} />
          </p>
        </li>
        <li>
          <p className="flush">{t("templates:publishStepDone")}</p>
        </li>
      </ol>
      <Link to="/docs#templates-publish" className="small">
        {t("templates:publishGuide")} →
      </Link>
    </div>
  );
}

/** A template's sign in its row's box: a package, not its name cut to fit (the name is whole under its title). */
export function TemplateIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z" />
      <path d="M4 7.5l8 4.5 8-4.5M12 12v9" />
    </svg>
  );
}

/** A row's Open, and Download of its latest version once one is uploaded: what any row of a template offers. */
export function templateItems(x: AppTemplate): MenuItem[] {
  return [
    { label: t("templates:templateOpen"), onSelect: () => navigate(`/t/${encodeURIComponent(x.id)}`) },
    ...(x.latest ? [{ label: t("templates:templateDownload", { n: x.latest }), onSelect: () => download(appTemplateArchiveURL(x.id, x.latest)) }] : []),
  ];
}

/** A template as /templates lists it, and a class's pages too (byj-1wv.77), with the menu its page gives it. */
export function TemplateRow({ x, menu }: { x: AppTemplate; menu: MenuItem[] }) {
  const path = `/t/${encodeURIComponent(x.id)}`;
  const title = x.title || x.name;
  return (
    <li className={`share-row template-row ${x.latest ? "is-live" : "is-ended"}`}>
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        <TemplateIcon />
      </span>
      <div className="row-main">
        <Link to={path} className="row-title">
          {title}
        </Link>
        {/* its name under its title, unless the name is the title already */}
        <div className="row-sub">
          {[
            x.title ? <code key="name">{x.name}</code> : null,
            !x.mine ? t("templates:templateBy", { owner: x.owner }) : null,
            x.description || null,
          ]
            .filter((p) => p !== null)
            .map((p, i) => (
              <span key={i}>
                {i > 0 && " · "}
                {p}
              </span>
            ))}
        </div>
      </div>
      {/* each in a column of its own, so the rows line up; narrow, an empty one takes no room */}
      <div className="row-meta">
        <span className="row-via">{x.via && <span className="chip">{phrases.templateVia(x.via)}</span>}</span>
        <span className="row-version">
          <span className={`chip${x.latest ? "" : " chip-warn"}`}>{x.latest ? t("templates:templateVersion", { n: x.latest }) : t("templates:templateNoVersion")}</span>
        </span>
        <time className="row-time" dateTime={x.updated_at} title={t("templates:templateUpdated", { w: when(x.updated_at) })}>
          {whenShort(x.updated_at)}
        </time>
      </div>
      <RowMenu label={t("shares:moreFor", { title })} items={menu} />
    </li>
  );
}
